class KarateKittyFeatures {
    constructor() {
        this.currentSlide = 0;
        this.slides = [];
        this.iconButtons = [];
        this.swiperWrapper = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        this.isTransitioning = false;
        this.autoplayInterval = null;
        this.autoplayDelay = 8000; // 8 seconds

        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        this.swiperWrapper = document.querySelector('.features-swiper-wrapper');
        this.slides = Array.from(document.querySelectorAll('.features-slide'));
        this.iconButtons = Array.from(document.querySelectorAll('.feature-icon-btn'));

        if (!this.swiperWrapper || this.slides.length === 0) {
            console.warn('KarateKittyFeatures: Required elements not found');
            return;
        }

        this.setupSlides();
        this.setupIconButtons();
        this.setupSlideClickHandlers();
        this.setupTouchEvents();
        this.setupKeyboardEvents();
        this.startAutoplay();

        // Show first slide
        this.goToSlide(0);
    }

    setupSlides() {
        // Initialize wrapper position
        this.swiperWrapper.style.transform = 'translateX(0%)';

        // Ensure all slides are visible (no need for active class on slides)
        for (const slide of this.slides) {
            slide.style.display = 'block';
        }
    }

    setupIconButtons() {
        for (let i = 0; i < this.iconButtons.length; i++) {
            const btn = this.iconButtons[i];
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.stopAutoplay();
                this.goToSlide(i);
                // Restart autoplay after user interaction
                setTimeout(() => this.startAutoplay(), 3000);
            });
        }
    }

    setupSlideClickHandlers() {
        for (let i = 0; i < this.slides.length; i++) {
            const slide = this.slides[i];
            slide.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Only navigate if clicking on a different slide
                if (i !== this.currentSlide) {
                    this.stopAutoplay();
                    this.goToSlide(i);
                    // Restart autoplay after user interaction
                    setTimeout(() => this.startAutoplay(), 3000);
                }
            });
        }
    }

    setupTouchEvents() {
        const container = document.querySelector('.features-swiper-container');
        if (!container) return;

        // Touch events
        container.addEventListener('touchstart', (e) => {
            this.touchStartX = e.touches[0].clientX;
            this.touchStartY = e.touches[0].clientY;
            this.stopAutoplay();
        });

        container.addEventListener('touchmove', (e) => {
            if (this.touchStartX !== 0 && this.touchStartY !== 0) {
                const currentX = e.touches[0].clientX;
                const currentY = e.touches[0].clientY;
                const diffX = Math.abs(currentX - this.touchStartX);
                const diffY = Math.abs(currentY - this.touchStartY);

                // Only prevent default if horizontal movement is greater than vertical
                // This allows vertical scrolling while preventing horizontal scrolling during swipes
                if (diffX > diffY) {
                    e.preventDefault();
                }
            }
        });

        container.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].clientX;
            this.handleSwipe();
            // Reset touch coordinates
            this.touchStartY = 0;
            // Restart autoplay after touch interaction
            setTimeout(() => this.startAutoplay(), 3000);
        });

        // Mouse events for desktop
        container.addEventListener('mousedown', (e) => {
            this.touchStartX = e.clientX;
            this.touchStartY = e.clientY;
            this.stopAutoplay();
        });

        container.addEventListener('mousemove', (e) => {
            if (this.touchStartX !== 0 && this.touchStartY !== 0) {
                const currentX = e.clientX;
                const currentY = e.clientY;
                const diffX = Math.abs(currentX - this.touchStartX);
                const diffY = Math.abs(currentY - this.touchStartY);

                // Only prevent default if horizontal movement is greater than vertical
                if (diffX > diffY) {
                    e.preventDefault();
                }
            }
        });

        container.addEventListener('mouseup', (e) => {
            if (this.touchStartX !== 0) {
                this.touchEndX = e.clientX;
                this.handleSwipe();
                this.touchStartX = 0;
                this.touchStartY = 0;
                // Restart autoplay after mouse interaction
                setTimeout(() => this.startAutoplay(), 3000);
            }
        });

        container.addEventListener('mouseleave', () => {
            this.touchStartX = 0;
            this.touchStartY = 0;
        });
    }

    setupKeyboardEvents() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                this.stopAutoplay();
                this.previousSlide();
                setTimeout(() => this.startAutoplay(), 3000);
            } else if (e.key === 'ArrowRight') {
                this.stopAutoplay();
                this.nextSlide();
                setTimeout(() => this.startAutoplay(), 3000);
            }
        });
    }

    handleSwipe() {
        const swipeThreshold = 50;
        const swipeDistance = this.touchEndX - this.touchStartX;

        if (Math.abs(swipeDistance) > swipeThreshold) {
            if (swipeDistance > 0) {
                this.previousSlide();
            } else {
                this.nextSlide();
            }
        }
    }

    goToSlide(index) {
        if (this.isTransitioning || index === this.currentSlide) return;

        this.isTransitioning = true;

        // Calculate transform value - each slide is 20% width
        const translateX = -(index * 18.8);

        // Apply transform to wrapper
        this.swiperWrapper.style.transform = `translateX(${translateX}%)`;

        // Remove active class from all icons
        for (const btn of this.iconButtons) {
            btn.classList.remove('active');
        }

        // Add active class to current icon
        this.iconButtons[index].classList.add('active');

        // Update current slide
        this.currentSlide = index;

        // Add pulse animation to active icon
        this.iconButtons[index].classList.add('pulse');
        setTimeout(() => {
            this.iconButtons[index].classList.remove('pulse');
        }, 1000);

        // Reset transition flag
        setTimeout(() => {
            this.isTransitioning = false;
        }, 500);

        // Trigger custom event for other components
        this.dispatchSlideChangeEvent(index);
    }

    nextSlide() {
        const nextIndex = (this.currentSlide + 1) % this.slides.length;
        this.goToSlide(nextIndex);
    }

    previousSlide() {
        const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
        this.goToSlide(prevIndex);
    }

    startAutoplay() {
        this.stopAutoplay();
        this.autoplayInterval = setInterval(() => {
            this.nextSlide();
        }, this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    dispatchSlideChangeEvent(index) {
        const event = new CustomEvent('karateKittySlideChange', {
            detail: {
                currentSlide: index,
                feature: this.slides[index].dataset.feature
            }
        });
        document.dispatchEvent(event);
    }

    // Public methods for external control
    pause() {
        this.stopAutoplay();
    }

    resume() {
        this.startAutoplay();
    }

    getCurrentSlide() {
        return this.currentSlide;
    }

    getTotalSlides() {
        return this.slides.length;
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Check if the section exists on the page
    const karateKittySection = document.querySelector('.karate-kitty-features');
    if (karateKittySection) {
        window.karateKittyFeatures = new KarateKittyFeatures();
    }
});

// Pause autoplay when page is not visible
document.addEventListener('visibilitychange', () => {
    if (window.karateKittyFeatures) {
        if (document.hidden) {
            window.karateKittyFeatures.pause();
        } else {
            window.karateKittyFeatures.resume();
        }
    }
});

// Handle window resize
window.addEventListener('resize', () => {
    if (window.karateKittyFeatures) {
        // Debounce resize events
        clearTimeout(window.karateKittyResizeTimeout);
        window.karateKittyResizeTimeout = setTimeout(() => {
            // Refresh current slide position after resize
            window.karateKittyFeatures.goToSlide(window.karateKittyFeatures.getCurrentSlide());
        }, 250);
    }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = KarateKittyFeatures;
} 